import {useEffect, useState} from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    IconButton,
    CircularProgress,
    Alert,
    Stack,
    DialogContentText,
    Container
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CopyIcon from '@mui/icons-material/ContentCopy';
import WarningIcon from '@mui/icons-material/Warning';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import {
    APP_DATETIME_FORMAT,
    formatAppDateTime,
    formatAppDateTimeApi,
} from '../utils/dateFormats';
import { useSnackbar } from 'notistack';
import { tokenService } from '../api/token';
import { ApiToken } from '../types/entities/api-token.entity';
import { CreateApiTokenRequest } from '../types/requests/api-token.requests';
import { CreateApiTokenResponse } from '../types/responses/api-token.responses';
import { BotsPanel } from './BotsPanel';

export const ApiTokens = () => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isTokenModalOpen, setIsTokenModalOpen] = useState(false);
    const [newToken, setNewToken] = useState<string>('');
    const [formData, setFormData] = useState<CreateApiTokenRequest>({
        name: '',
        expiration: ''
    });
    const [errors, setErrors] = useState<Partial<CreateApiTokenRequest>>({});

    const { data: tokens, isLoading, error } = useQuery<ApiToken[]>({
        queryKey: ['tokens'],
        queryFn: tokenService.getAll
    });

    useEffect(() => {
        document.title = t('apiTokens.title');
    }, []);

    const createTokenMutation = useMutation({
        mutationFn: tokenService.create,
        onSuccess: (response: CreateApiTokenResponse) => {
            setNewToken(response.token);
            setIsCreateModalOpen(false);
            setIsTokenModalOpen(true);
            queryClient.invalidateQueries({ queryKey: ['tokens'] });
        },
        onError: () => {
            enqueueSnackbar(t('apiTokens.createError'), { variant: 'error' });
        }
    });

    const deleteTokenMutation = useMutation({
        mutationFn: tokenService.delete,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tokens'] });
            enqueueSnackbar(t('apiTokens.deleteSuccess'), { variant: 'success' });
        },
        onError: () => {
            enqueueSnackbar(t('apiTokens.deleteError'), { variant: 'error' });
        }
    });

    const handleCreateToken = () => {
        const newErrors: Partial<CreateApiTokenRequest> = {};
        
        if (!formData.name.trim()) {
            newErrors.name = t('apiTokens.validation.name.required');
        }
        
        if (!formData.expiration) {
            newErrors.expiration = t('apiTokens.validation.expiration.required');
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        createTokenMutation.mutate(formData);
    };

    const handleDeleteToken = (id: number) => {
        deleteTokenMutation.mutate(id);
    };

    const handleCopyToken = (token: string) => {
        navigator.clipboard.writeText(token);
        enqueueSnackbar(t('apiTokens.copySuccess'), { variant: 'success' });
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleDateChange = (date: Date | null) => {
        setFormData(prev => ({ 
            ...prev, 
            expiration: formatAppDateTimeApi(date) ?? '',
        }));
        setErrors(prev => ({ ...prev, expiration: '' }));
    };

    const handleCloseTokenModal = () => {
        setIsTokenModalOpen(false);
        setNewToken('');
        setFormData({ name: '', expiration: '' });
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mt: 2 }}>
                {t('common.error')}
            </Alert>
        );
    }

    const renderEmptyState = () => (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Stack spacing={2} alignItems="center">
                <Typography variant="h6" color="text.secondary">
                    {t('apiTokens.noTokens')}
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setIsCreateModalOpen(true)}
                    sx={{ mt: 2 }}
                >
                    {t('apiTokens.createButton')}
                </Button>
            </Stack>
        </Paper>
    );

    const renderTokenList = () => (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" component="h2">
                    {t('apiTokens.title')}
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setIsCreateModalOpen(true)}
                >
                    {t('apiTokens.createButton')}
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('apiTokens.name')}</TableCell>
                            <TableCell>{t('apiTokens.lastUsed')}</TableCell>
                            <TableCell>{t('apiTokens.expiresAt')}</TableCell>
                            <TableCell align="right">{t('apiTokens.actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tokens?.map((token) => (
                            <TableRow key={token.id}>
                                <TableCell>{token.name}</TableCell>
                                <TableCell>
                                    {token.last_used_at
                                        ? formatAppDateTime(token.last_used_at)
                                        : t('apiTokens.neverUsed')}
                                </TableCell>
                                <TableCell>
                                    {token.expires_at
                                        ? formatAppDateTime(token.expires_at)
                                        : t('apiTokens.neverUsed')}
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton
                                        color="error"
                                        onClick={() => handleDeleteToken(token.id)}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );

    return (
        <>
            <Container sx={{ mt: 4 }}>
                {!tokens?.length ? renderEmptyState() : renderTokenList()}
                <Dialog open={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)}>
                    <DialogTitle>{t('apiTokens.createTitle')}</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            label={t('apiTokens.form.name')}
                            name="name"
                            fullWidth
                            value={formData.name}
                            onChange={handleChange}
                            error={!!errors.name}
                            helperText={errors.name}
                        />
                        <DateTimePicker
                            label={t('apiTokens.form.expiration')}
                            value={formData.expiration ? new Date(formData.expiration.replace(' ', 'T')) : null}
                            onChange={handleDateChange}
                            format={APP_DATETIME_FORMAT}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    margin: 'dense',
                                    error: !!errors.expiration,
                                    helperText: errors.expiration,
                                    placeholder: t('apiTokens.form.expirationPlaceholder')
                                }
                            }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setIsCreateModalOpen(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            onClick={handleCreateToken}
                            variant="contained"
                            disabled={createTokenMutation.isPending}
                        >
                            {createTokenMutation.isPending ? (
                                <CircularProgress size={24} />
                            ) : (
                                t('apiTokens.createButton')
                            )}
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog open={isTokenModalOpen} onClose={handleCloseTokenModal}>
                    <DialogTitle>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <WarningIcon color="warning" />
                            <Typography>{t('apiTokens.tokenWarning.title')}</Typography>
                        </Stack>
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {t('apiTokens.tokenWarning.description')}
                        </DialogContentText>
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body2" sx={{ wordBreak: 'break-all', flex: 1 }}>
                                    {newToken}
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={() => handleCopyToken(newToken)}
                                >
                                    <CopyIcon />
                                </IconButton>
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseTokenModal}>
                            {t('common.close')}
                        </Button>
                    </DialogActions>
                </Dialog>
            <BotsPanel />
            </Container>
        </>
    );
}; 
